import { Color, Graph, Shape } from "@antv/x6";
import { Stencil } from "@antv/x6-plugin-stencil";
import { Transform } from "@antv/x6-plugin-transform";
import { Selection } from "@antv/x6-plugin-selection";
import { Snapline } from "@antv/x6-plugin-snapline";
import { Keyboard } from "@antv/x6-plugin-keyboard";
import { Clipboard } from "@antv/x6-plugin-clipboard";
import { History } from "@antv/x6-plugin-history";
import { Dnd } from "@antv/x6-plugin-dnd";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  App,
  Button,
  Col,
  ColorPicker,
  Flex,
  Form,
  message,
  Modal,
  Row,
  Select,
  Skeleton,
  Typography,
} from "antd";
import "../../../../src/styles/Configuration/SingleLine.css";
import { theme } from "antd";
import registerNode from "../../../functions/single_line_diagram/registerNode";
import { ArrowLeftOutlined, ContactsOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import initStencil from "../../../functions/single_line_diagram/initStencil";
import registerGraphEventListener from "../../../functions/single_line_diagram/registerGraphEventListener";
import { IndexContext } from "../../../context/IndexContext";
import EdgeEditingModal from "../../../components/configuration/single_line_diagram/EdgeEditingModal";
import { center } from "@antv/x6/lib/registry/node-anchor/bbox";
import { bottom, right } from "@antv/x6/lib/registry/port-layout/line";
import configurePowerMeterNode from "../../../functions/single_line_diagram/configurePowerMeterNode";

const RESIZABLE_SHAPE = ["text-box", "rect", "circle"];
const NON_PRESERVE_ASPECT_RATIO_SHAPE = ["text-box", "rect"];

const { useToken } = theme;
const { Text, Title } = Typography;

// #region 初始化图形

export default function SingleLineDiagramConfigurationPage() {
  const { message, modal, notification } = App.useApp();
  const navigate = useNavigate();
  const [fetchWrapper] = useFetch();
  const { messageApi, siteSelected, modalApi } = useContext(IndexContext);
  let { sldID } = useParams();
  const graphRef = useRef(null);
  const stencilRef = useRef(null);
  const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [sld, setSld] = useState(undefined);
  const powerMeterSelection = useRef(null);
  const [loading, setLoading] = useState(false);

  function getSLD() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/getSingleLineDiagramByID",
      method: "GET",
      params: { ID: sldID },
      onSuccess: (value) => {
        setSld(value.data);
      },
      onResponseError: (error) => {
        setSld(false);
      },
    });
  }

  function getPowerMeterSelection() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/getPowerMeterSelection",
      method: "GET",
      params: { SiteID: sld?.SiteID },
      onSuccess: (value) => {
        powerMeterSelection.current = value.data;
      },
      onResponseError: (error) => {
        powerMeterSelection.current = [];
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  function onSave() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "single_line_diagram/updateSingleLineDiagram",
      method: "PUT",
      body: { id: sldID, cells: graphRef.current.toJSON().cells },
      onSuccess: (value) => {
        setSld(value.data);
        localStorage.removeItem("sld.clipboard.cells");
        messageApi.open({
          type: "success",
          content: value.message,
        });
      },
      onResponseError: (error) => {
        messageApi.open({
          type: "error",
          content: error.error,
        });
      },
      onFetchError: (error) => {
        messageApi.open({
          type: "error",
          content: error.error,
        });
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  function onCancel() {
    const graph = graphRef.current;
    graph.fromJSON(sld.Cells);
    graphRef.current = graph;
    localStorage.removeItem("sld.clipboard.cells");
  }

  const showEdgeModal = () => {
    setIsEdgeModalOpen(true);
  };

  const handleEdgeOk = (value) => {
    Object.keys(value).forEach((key) => {
      selectedEdge.setAttrByPath(key, value[key]);
    });
    setIsEdgeModalOpen(false);
  };

  const handleEdgeCancel = () => {
    setIsEdgeModalOpen(false);
  };

  useEffect(() => {
    const localCache = localStorage.getItem("sld.clipboard.cells");
    if (localCache != null && graphRef.current != null) {
      const graph = graphRef.current;
      const json = JSON.parse(localCache);
      graph.fromJSON(json.cells);
      graph.zoomToFit({
        padding: { left: 8, top: 8, right: 120, bottom: 8 },
        minScale: 0.75,
        maxScale: 1,
        preserveAspectRatio: true,
      });
    } else if (sld != undefined && graphRef.current != null) {
      const graph = graphRef.current;
      graph.fromJSON(sld.Cells);
      graph.zoomToFit({
        padding: { left: 8, top: 8, right: 120, bottom: 8 },
        minScale: 0.75,
        maxScale: 1,
        preserveAspectRatio: true,
      });
    }
    getPowerMeterSelection();
  }, [sld]);

  useEffect(() => {
    getSLD();

    const container = document.getElementById("container");
    container.innerHTML = "";
    const stencilContainer = document.createElement("div");
    stencilContainer.id = "stencil";
    const graphContainer = document.createElement("div");
    graphContainer.id = "graph-container";
    container.appendChild(stencilContainer);
    container.appendChild(graphContainer);

    registerNode();

    const graph = new Graph({
      container: document.getElementById("graph-container"),
      grid: true,
      mousewheel: {
        enabled: true,
        zoomAtMousePosition: true,
        modifiers: "ctrl",
        minScale: 0.75,
        maxScale: 1,
      },
      connecting: {
        router: "manhattan",
        connector: {
          name: "rounded",
          args: {
            radius: 8,
          },
        },
        anchor: "center",
        connectionPoint: "anchor",
        allowBlank: false,
        snap: {
          radius: 20,
        },
        createEdge() {
          return new Shape.Edge({
            attrs: {
              line: {
                stroke: "var(--ant-color-text-base)",
                strokeDasharray: 0,
                style: {
                  animation: "ant-line 30s infinite linear",
                },
                strokeWidth: 2,
                sourceMarker: {
                  name: "block",
                  width: 1,
                  height: 1,
                },
                targetMarker: {
                  name: "block",
                  width: 1,
                  height: 1,
                },
              },
            },
            zIndex: 0,
            tools: ["vertices", "segments"],
          });
        },
        validateConnection({ targetMagnet }) {
          return !!targetMagnet;
        },
      },
      highlighting: {
        magnetAdsorbed: {
          name: "stroke",
          args: {
            attrs: {
              fill: "#5F95FF",
              stroke: "#5F95FF",
            },
          },
        },
      },
    });

    graph
      .use(
        new Transform({
          resizing: {
            enabled: (node) => {
              return RESIZABLE_SHAPE.includes(node.shape);
            },
            preserveAspectRatio: (node) => {
              return !NON_PRESERVE_ASPECT_RATIO_SHAPE.includes(node.shape);
            },
          },
          rotating: true,
        })
      )
      .use(
        new Selection({
          rubberband: true,
          showNodeSelectionBox: true,
          showEdgeSelectionBox: true,
        })
      )
      .use(new Snapline({ enabled: true, sharp: true }))
      .use(new Keyboard())
      .use(new Clipboard())
      .use(new History());

    const stencil = new Stencil({
      title: "Component",
      target: graph,
      search: (cell, keyword, groupName, stencil) => {
        if (keyword) {
          return cell.shape.indexOf(keyword) !== -1;
        }
        return true;
      },
      placeholder: "Search",
      notFoundText: "Not Found",
      stencilGraphWidth: 250,
      stencilGraphHeight: 280,
      collapsable: false,
      groups: [
        {
          title: "General",
          name: "general",
          graphWidth: "100%",
          graphHeight: 0,
          layoutOptions: {
            columns: 3,
            center: true,
            columnWidth: 70,
            rowHeight: "auto",
          },
        },
        {
          title: "Power Meter",
          name: "power-meter",
          graphHeight: 0,
          layoutOptions: {
            columns: 1,
            columnWidth: 125,
            rowHeight: "compact",
          },
        },
        {
          title: "Transformer",
          name: "transformers",
          graphHeight: 0,
          layoutOptions: {
            columns: 1,
            columnWidth: "auto",
            rowHeight: "compact",
          },
        },
        {
          title: "Switches",
          name: "switches",
          graphHeight: 0,
          layoutOptions: {
            columns: 1,
            dx: 20,
            columnWidth: "auto",
            rowHeight: "compact",
          },
        },
      ],
    });

    document.getElementById("stencil").appendChild(stencil.container);

    registerGraphEventListener(graph);
    initStencil(graph, stencil, modal);

    graph.on("edge:dblclick", ({ edge, e }) => {
      setSelectedEdge(edge);
      showEdgeModal();
    });

    graph.on("cell:change:*", () => {
      const cell = JSON.stringify(graph.toJSON() ?? null);
      localStorage.setItem("sld.clipboard.cells", cell);
    });

    graphRef.current = graph;
    stencilRef.current = stencil;

    graphRef.current.on("node:dblclick", ({ node }) => {
      if (["power-meter-simple", "power-meter-detail"].includes(node.shape)) {
        configurePowerMeterNode(
          node,
          graphRef.current,
          modalApi,
          powerMeterSelection.current
        );
      }
    });

    graphRef.current.on("node:added", ({ node }) => {
      if (["power-meter-simple", "power-meter-detail"].includes(node.shape)) {
        configurePowerMeterNode(
          node,
          graphRef.current,
          modalApi,
          powerMeterSelection.current
        );
      }
    });

    return () => {
      graphRef.current?.off();
      localStorage.removeItem("sld.clipboard.cells");
    };
  }, []);

  return (
    <>
      <Row gutter={[12, 12]} style={{ overflowX: "hidden" }}>
        <Col span={24}>
          <Title level={3} className="mt-2 mb-5 ml-5">
            <ArrowLeftOutlined
              className="mr-3"
              onClick={() => {
                if (sld == undefined || null) {
                  navigate(`..?Key=Site`);
                } else {
                  navigate(`../Site/${sld?.SiteID}?Key=SLD`);
                }
              }}
            />
            Single Line Diagram - {sld?.Name}
          </Title>
        </Col>
      </Row>
      <Row gutter={[12, 12]}>
        <Col span={24} style={{ height: "80vh" }}>
          <div id="container"></div>
        </Col>
        <Col span={24}>
          <Flex gap="middle" justify="flex-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={onSave} loading={loading}>
              Save
            </Button>
          </Flex>
        </Col>
      </Row>
      <EdgeEditingModal
        isEdgeModalOpen={isEdgeModalOpen}
        handleEdgeCancel={handleEdgeCancel}
        handleEdgeOk={handleEdgeOk}
        selectedEdge={selectedEdge}
      />
    </>
  );
}
